import {
  Phase2,
  AgFinancingPhase2Status,
  AgFinancingCollateralAnalysisStatus,
  AgFinancingCreditAnalysisStatus,
  AgOrderInfo,
  AgFinancingCreditApprovalStatus,
  AgFinancingInput,
  FieldMonitoringStatus,
  AgFinancing,
  CreditApprovalData,
  CreditApprovalRoundStatus
} from '@indigo-ag/schema';

export class Phase2Util {
  static isPhase2FieldMonitoringComplete(phase2: Phase2): boolean {
    const fieldMonitoring = phase2?.fieldMonitoring;

    return !!fieldMonitoring?.completedAt;
  }

  static isPhase2CollateralAnalysisComplete(phase2: Phase2): boolean {
    const collateral = phase2?.collateral;

    return (
      collateral?.status === AgFinancingCollateralAnalysisStatus.COMPLETED ||
      collateral?.status === AgFinancingCollateralAnalysisStatus.APPROVED
    );
  }

  static isPhase2CreditComplete(phase2: Phase2): boolean {
    const credit = phase2?.credit;

    return (
      credit?.status === AgFinancingCreditAnalysisStatus.COMPLETED ||
      credit?.status === AgFinancingCreditAnalysisStatus.APPROVED
    );
  }

  static canOnHold(phase2?: Phase2): boolean {
    return (
      !phase2?.status ||
      phase2?.status === AgFinancingPhase2Status.IN_PROGRESS ||
      phase2?.status === AgFinancingPhase2Status.PENDING_ACTIVATION ||
      phase2?.status === AgFinancingPhase2Status.NOT_STARTED ||
      phase2?.status === AgFinancingPhase2Status.AWAITING_COMMERCIAL_DEAL
    );
  }

  /**
   * This function tells if a given orderInfo has commercial approval on salesforce
   * @param orderInfo
   * @returns true if all opportunities in OrderInfo has the property hasCommercialApproval are true otherwise false
   */
  static getCommercialApproval = (orderInfo?: AgOrderInfo): boolean => {
    if (!orderInfo || !orderInfo.opportunities || !orderInfo.opportunities.length) {
      return false;
    }

    return orderInfo.opportunities.every(opp => opp.hasCommercialApproval);
  };

  static canEscalateToCM(status?: AgFinancingPhase2Status): boolean {
    return status === AgFinancingPhase2Status.IN_PROGRESS;
  }

  static isWaitingCMApproval(status?: AgFinancingPhase2Status): boolean {
    return status === AgFinancingPhase2Status.WAITING_COMMITTEE_DECISION;
  }

  static isApproved(status?: AgFinancingPhase2Status): boolean {
    return status === AgFinancingPhase2Status.APPROVED;
  }

  static isCreditApprovalWaitingCADecision(creditApprovalStatus?: AgFinancingCreditApprovalStatus): boolean {
    return creditApprovalStatus === AgFinancingCreditApprovalStatus.IN_PROGRESS;
  }

  static isCreditApprovalWaitingCMDecision(creditApprovalStatus?: AgFinancingCreditApprovalStatus): boolean {
    return creditApprovalStatus === AgFinancingCreditApprovalStatus.WAITING_CREDIT_MANAGER_DECISION;
  }

  static isPhase2CreditApprovalComplete(
    phase2: Phase2,
    creditApprovalDataRound?: CreditApprovalData['round']
  ): boolean {
    const creditApprovalStatus = phase2?.creditApprovalStatus;
    const legacyStatusValidation =
      creditApprovalStatus === AgFinancingCreditApprovalStatus.CREDIT_ANALYST_APPROVED ||
      creditApprovalStatus === AgFinancingCreditApprovalStatus.CREDIT_MANAGER_APPROVED;

    return creditApprovalDataRound
      ? creditApprovalDataRound.status === CreditApprovalRoundStatus.COMPLETE
      : legacyStatusValidation;
  }

  static hasPromissoryNote(agFinance?: AgFinancing): boolean {
    return !!agFinance?.orderInfo?.opportunities?.reduce(
      (p, c) => (p = (c.collateralType?.includes('Promissory Note') || p) ?? false),
      false
    );
  }

  static autoStartPhase2(
    input: Partial<AgFinancingInput> = {},
    creditAnalysisStart: boolean,
    collateralAnalysisStart: boolean,
    georeferenceStart: boolean,
    name: string
  ): Partial<AgFinancingInput> {
    // Always initialize input. When this process is executed manually, the start flags might not follow a particular
    // order. i.e. when trying to start the FM analysis process manually first, phase2.status and phase2.fieldMonitoring
    // have to be set in PROGRESS, no matter other flags value.
    input.phase2 = {
      status: AgFinancingPhase2Status.IN_PROGRESS
    };

    if (creditAnalysisStart) {
      input.phase2.credit = {
        isDone: false,
        startedAt: new Date(),
        startedBy: name,
        status: AgFinancingCreditAnalysisStatus.STARTED
      };
    }

    if (collateralAnalysisStart) {
      input.phase2.collateral = {
        startedAt: new Date(),
        startedBy: name
      };
    }

    if (georeferenceStart) {
      input.phase2.fieldMonitoring = {
        status: FieldMonitoringStatus.IN_PROGRESS
      };
    }
    return input;
  }
}
